import React, { useEffect, useRef } from 'react'
import SliceAccordion from '../SliceAccordion'
import SliceContentArea from '../SliceContentArea'
import SliceFeatureCard from '../SliceFeatureCard'
import SliceForm from '../SliceForm'
import SliceVideo from '../SliceVideo'
import SliceIconGrid from '../SliceIconGrid'
import SliceLogoCarousel from '../SliceLogoCarousel'
import SliceTestimonialCarousel from '../SliceTestimonialCarousel'
import SliceCards from '../SliceCards'
import SliceLevelChart from '../SliceLevelChart'
import SliceGoogleReviews from '../SliceGoogleReviews'
import SliceBannerPromotion from '../SliceBannerPromotion'

import { graphql } from 'gatsby'

const sliceMap = {
  cards: SliceCards,
  accordion: SliceAccordion,
  content_area: SliceContentArea,
  feature_card: SliceFeatureCard,
  form: SliceForm,
  logo_carousel: SliceLogoCarousel,
  testimonial_carousel: SliceTestimonialCarousel,
  icon_grid: SliceIconGrid,
  video: SliceVideo,
  level_chart: SliceLevelChart,
  google_reviews: SliceGoogleReviews,
  promo_banner: SliceBannerPromotion,
}

const SliceWrapper = ({ id, slice, lang, pageLinks, removeTopPadding }) => {
  const sectionRef = useRef()

  useEffect(() => {
    if (removeTopPadding && sectionRef.current) {
      const firstSectionFound = sectionRef.current.querySelector('section')
      if (firstSectionFound) {
        firstSectionFound.style.paddingTop = 0
      }
    }
  }, [removeTopPadding])

  return (
    <section id={id} ref={sectionRef}>
      {React.createElement(sliceMap[slice.slice_type], {
        ...slice,
        lang,
        pageLinks,
      })}
    </section>
  )
}

const Slices = ({ slices = [], pageLinks, lang }) => {
  return slices.map((slice, sliceIndex) => {
    if (!sliceMap?.[slice?.slice_type]) return null
    const id = `slice-${slice.slice_type}-${sliceIndex}`
    return (
      <SliceWrapper
        key={id}
        id={id}
        slice={slice}
        lang={lang}
        pageLinks={pageLinks}
        removeTopPadding={
          slices[sliceIndex - 1]?.primary?.join_block_below || false
        }
      />
    )
  })
}

export const query = graphql`
  fragment Slices on PrismicPageDataBodySlicesType {
    ... on PrismicPageDataBodyGoogleReviews {
      slice_type
      primary {
        join_block_below
        reviews_subtitle {
          html
          text
        }
        reviews_title {
          html
          text
        }
      }
    }
    ... on PrismicPageDataBodyAccordion {
      slice_type
      primary {
        enable_structured_data
        title1
        subtitle1 {
          html
          text
        }
        section_background_color
        join_block_below
      }
      items {
        accordion_title
        accordion_content {
          richText
          text
          html
          raw
        }
      }
    }
    ... on PrismicPageDataBodyIconGrid {
      slice_type
      primary {
        title1
        subtitle1 {
          html
          text
        }
        section_background_color
        join_block_below
      }
      items {
        icon_name
        icon_title
        icon_link {
          url
        }
        icon_content
      }
    }
    ... on PrismicPageDataBodyContentArea {
      slice_type
      primary {
        content {
          text
          raw
        }
        section_background_color
        join_block_below
      }
    }
    ... on PrismicPageDataBodyLevelChart {
      slice_type
      primary {
        level_chart_title
        level_chart_subtitle {
          text
          html
        }
        chart_helper_label
        section_background_color
        join_block_below
      }
      items {
        level
        group_name
        custom_level_title
        content {
          text
          html
          richText
        }
      }
    }
    ... on PrismicPageDataBodyFeatureCard {
      slice_type
      primary {
        feature_card_title
        feature_card_content {
          text
          raw
        }
        feature_card_image {
          url
        }
        flipped
        youtube_video_id
        feature_card_call_to_action_text
        feature_card_call_to_action {
          url
          target
          url
        }
        one_page_link
        section_background_color
        join_block_below
      }
    }
    ... on PrismicPageDataBodyLogoCarousel {
      slice_type
      primary {
        logo_carousel_title
        logo_carousel_subtitle {
          html
          richText
        }
        section_background_color
        join_block_below
      }
      items {
        logo {
          url
        }
      }
    }
    ... on PrismicPageDataBodyTestimonialCarousel {
      slice_type
      primary {
        testimonial_carousel_title
        testimonial_carousel_subtitle {
          html
          richText
        }
        section_background_color
        join_block_below
      }
      items {
        image {
          url
        }
        testimonial_title
        testimonial_subtitle
        testimonial_content {
          html
          richText
        }
      }
    }
    ... on PrismicPageDataBodyForm {
      slice_type
      primary {
        section_background_color
        join_block_below
        form_type {
          document {
            ... on PrismicForm {
              data {
                form_title
                form_subtitle {
                  html
                }
                submit_button
                body {
                  ... on PrismicFormDataBodyField {
                    slice_type
                    primary {
                      field_label
                      field_width
                    }
                  }
                }
              }
            }
          }
        }
        thank_modal {
          richText
        }
        error_modal {
          richText
        }
      }
    }
    ... on PrismicPageDataBodyCards {
      id
      slice_type
      primary {
        cards_title
        cards_subtitle {
          richText
          raw
        }
        section_background_color
        join_block_below
      }
      items {
        one_page_link
        card_button_text
        card_content {
          text
          raw
        }
        card_image {
          url
        }
        card_link {
          url
        }
        card_title
        title_highlight
      }
    }

    ... on PrismicPageDataBodyPromoBanner {
      slice_type
      primary {
        banner_subtitle {
          text
        }
        background_color
        section_background_color
        join_block_below
        banner_title {
          text
        }
        external_link {
          url
        }
        internal_link {
          url
        }
        image {
          url
          alt
        }
      }
    }
    ... on PrismicPageDataBodyVideo {
      slice_type
      primary {
        section_background_color
        join_block_below
        youtube_video_id
      }
    }
  }
`

export default Slices
